import { Flex } from 'antd';
import React, { useState } from 'react';
import { AiPanelBulletItem } from '@tools/shared';
import {
  IconSize,
  IconType,
  IconWrap,
  semanticTheme,
  Typography
} from '@tools/ui/dime';
import useT from '@tools/ui-hooks/useT';
import { ShowMoreButton } from './AiPanelBlock.styles';
import { AiPanelLinkView } from './AiPanelLinkView';

type BulletListBlockProps = {
  items: AiPanelBulletItem[];
  maxItems?: number;
  charLimit?: number;
};

const normalizeItems = (items: AiPanelBulletItem[]): AiPanelBulletItem[] =>
  items.map((item) => (typeof item === 'string' ? { text: item } : item));

const BulletListBlock: React.FC<BulletListBlockProps> = ({
  items,
  maxItems,
  charLimit
}) => {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);

  const normalized = normalizeItems(items);
  const collapsed: AiPanelBulletItem[] = [];
  let charCount = 0;
  for (const item of normalized) {
    if (maxItems !== undefined && collapsed.length >= maxItems) {
      break;
    }
    if (charLimit !== undefined && charCount + item.text.length > charLimit) {
      break;
    }
    collapsed.push(item);
    charCount += item.text.length;
  }

  const canTruncate = collapsed.length < normalized.length;
  const visible = expanded || !canTruncate ? normalized : collapsed;

  return (
    <>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {visible.map((item, i) => (
          <li key={i}>
            <Flex align="center" gap={semanticTheme.dime.spacing.s}>
              {item.link ? (
                <AiPanelLinkView link={item.link} />
              ) : (
                <Typography variant="body1">{item.text}</Typography>
              )}
              {!!item.generatedByAi && (
                <IconWrap
                  type={IconType.AiSparkle}
                  size={IconSize.Small}
                  color={semanticTheme.dime.colors.text.secondary}
                />
              )}
            </Flex>
          </li>
        ))}
      </ul>
      {!!canTruncate && (
        <ShowMoreButton onClick={() => setExpanded((prev) => !prev)}>
          <Typography
            variant="body1"
            color={semanticTheme.dime.colors.link.primary}
          >
            {expanded ? t('showLess') : t('showMore')}
          </Typography>
          <IconWrap
            type={expanded ? IconType.ChevronUp : IconType.ChevronDown}
            size={IconSize.Small}
            color={semanticTheme.dime.colors.link.primary}
          />
        </ShowMoreButton>
      )}
    </>
  );
};

export default BulletListBlock;
